import { useQuery } from "@tanstack/react-query";
import { useToast } from "@repo/ui";
import { configurationApi } from "~/api";
import type { Status } from "~/types/api";

export const getStatusesQueryKey = (workflowId: number) =>
  configurationApi.queryOptions("get", "/api/workflow/{workflowId}/Status", {
    params: { path: { workflowId } },
  }).queryKey;

export const fetchStatuses = async (workflowId: number): Promise<Status[]> => {
  const response = await configurationApi.client.GET("/api/workflow/{workflowId}/Status", {
    params: { path: { workflowId } },
  });

  if (response.error) {
    throw new Error(
      `Failed to fetch statuses for workflow ${workflowId}: ${response.error.toString()}`
    );
  }

  if (!response.data) {
    throw new Error(`Statuses for workflow ${workflowId} not found`);
  }

  return response.data as Status[];
};

export const useWorkflowStatuses = (workflowId: number, enabled = true) => {
  const { showToast } = useToast();

  const { data, isLoading, error, refetch } = useQuery<Status[]>({
    queryKey: getStatusesQueryKey(workflowId),
    queryFn: async () => {
      try {
        return await fetchStatuses(workflowId);
      } catch (err) {
        const errorMessage = (err as Error).message;
        showToast("error", errorMessage, "Error fetching statuses");
        throw err;
      }
    },
    enabled: enabled && workflowId > 0,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });

  return {
    statuses: data,
    isLoading,
    error,
    refetch,
  };
};

export const useMultipleWorkflowStatuses = (workflowIds: number[], enabled = true) => {
  const { showToast } = useToast();

  const { data, isLoading, error, refetch } = useQuery<Status[]>({
    queryKey: ["workflow-statuses-multiple", ...workflowIds.sort()],
    queryFn: async () => {
      try {
        const statusPromises = workflowIds.map((workflowId) => fetchStatuses(workflowId));
        const responses = await Promise.all(statusPromises);
        return responses.flat();
      } catch (err) {
        const errorMessage = (err as Error).message;
        showToast("error", errorMessage, "Error fetching multiple workflow statuses");
        throw err;
      }
    },
    enabled: enabled && workflowIds.length > 0,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });

  return {
    statuses: data,
    isLoading,
    error,
    refetch,
  };
};
